import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Users, CreditCard, Check } from 'lucide-react';
import axios from 'axios';

const OrganizationRegisterPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    org_type: '',
    sport: '',
    billing_path: 'athlete_funded',
    contact_name: '',
    contact_email: '',
    password: ''
  });

  // Effect to navigate after user state is updated
  useEffect(() => {
    if (registrationComplete && user && user.role === 'org_admin') {
      navigate('/organization', { replace: true });
    }
  }, [registrationComplete, user, navigate]);

  const orgTypes = [
    { value: 'school', label: 'School / High School' },
    { value: 'club', label: 'Club / Travel Team' },
    { value: 'academy', label: 'Academy / Training Facility' },
    { value: 'league', label: 'League / Association' },
  ];

  const sports = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Swimming',
    'Track & Field', 'Tennis', 'Volleyball', 'Wrestling', 
    'Multi-Sport', 'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/organizations/register`, formData);
      
      // Store token first
      localStorage.setItem('aura_token', response.data.token);
      
      // Build complete user data
      const userData = {
        ...response.data.user,
        org_id: response.data.organization.org_id
      };
      
      // Show success toast
      toast.success(`Welcome to Aura! Your invite code is: ${response.data.organization.invite_code}`);
      
      // Update user context - this will trigger the useEffect to navigate
      updateUser(userData);
      setRegistrationComplete(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-aura-bg flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/register')}
          className="flex items-center gap-2 text-aura-text-muted hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-aura-primary rounded-sm flex items-center justify-center">
            <span className="font-heading font-black text-white text-lg">A</span>
          </div>
        </Link>
      </header>

      {/* Progress Steps */}
      <div className="px-4 py-2">
        <div className="max-w-md mx-auto flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${s <= step ? 'bg-aura-secondary' : 'bg-white/10'}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Step 1: Organization Info */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-12 h-12 mx-auto rounded-lg bg-aura-secondary/20 flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-aura-secondary" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                  Organization Info
                </h1>
                <p className="text-aura-text-muted">Tell us about your program</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Organization Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Lincoln High School Athletics"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white"
                    data-testid="org-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Organization Type</Label>
                  <Select value={formData.org_type} onValueChange={(v) => setFormData({ ...formData, org_type: v })}>
                    <SelectTrigger className="h-12 bg-aura-surface border-white/10 text-white" data-testid="org-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {orgTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Primary Sport</Label>
                  <Select value={formData.sport} onValueChange={(v) => setFormData({ ...formData, sport: v })}>
                    <SelectTrigger className="h-12 bg-aura-surface border-white/10 text-white">
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {sports.map((sport) => (
                        <SelectItem key={sport} value={sport.toLowerCase()} className="text-white">
                          {sport}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!formData.name || !formData.org_type}
                  className="w-full h-12 bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Billing Path */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-12 h-12 mx-auto rounded-lg bg-aura-secondary/20 flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-aura-secondary" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                  Choose Your Path
                </h1>
                <p className="text-aura-text-muted">How would you like to handle billing?</p>
              </div>

              <RadioGroup
                value={formData.billing_path}
                onValueChange={(v) => setFormData({ ...formData, billing_path: v })}
                className="space-y-4"
              >
                {/* Path A: Org-Funded */}
                <label 
                  className={`block cursor-pointer aura-card p-5 transition-all ${
                    formData.billing_path === 'org_funded' ? 'border-aura-secondary ring-1 ring-aura-secondary' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <RadioGroupItem value="org_funded" className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-heading font-bold text-lg text-white uppercase">Path A: Org-Funded</h3>
                        <span className="text-aura-secondary font-heading font-bold">$49 + $2/athlete</span>
                      </div>
                      <p className="text-sm text-aura-text-muted mb-3">
                        Your organization pays for all athletes. Centralized billing, easier accounting.
                      </p>
                      <ul className="text-xs text-aura-text-subtle space-y-1">
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          Platform fee: $49/month
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          Per athlete: $2/month
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          50 athletes = $149/month
                        </li>
                      </ul>
                    </div>
                  </div>
                </label>

                {/* Path B: Athlete-Funded */}
                <label 
                  className={`block cursor-pointer aura-card p-5 transition-all ${
                    formData.billing_path === 'athlete_funded' ? 'border-aura-success ring-1 ring-aura-success' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <RadioGroupItem value="athlete_funded" className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-heading font-bold text-lg text-white uppercase">Path B: Athlete-Funded</h3>
                        <span className="text-aura-success font-heading font-bold">FREE for org</span>
                      </div>
                      <p className="text-sm text-aura-text-muted mb-3">
                        Athletes/parents pay individually. Zero budget required from your organization.
                      </p>
                      <ul className="text-xs text-aura-text-subtle space-y-1">
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          Platform fee: $0
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          Athletes pay: $3/month each
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-aura-success" />
                          No budget approval needed
                        </li>
                      </ul>
                    </div>
                  </div>
                </label>
              </RadioGroup>

              <Button
                onClick={() => setStep(3)}
                className="w-full h-12 mt-6 bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 3: Admin Account */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-12 h-12 mx-auto rounded-lg bg-aura-secondary/20 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-aura-secondary" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                  Admin Account
                </h1>
                <p className="text-aura-text-muted">Create your administrator login</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Your Name</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Coach / Athletic Director name"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="admin@school.edu"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a password"
                    required
                    minLength={6}
                    className="h-12 bg-aura-surface border-white/10 text-white"
                    data-testid="org-password-input"
                  />
                </div>

                {/* Summary */}
                <Card className="bg-aura-surface/50 border-white/10">
                  <CardContent className="p-4">
                    <h4 className="font-heading font-bold text-sm text-aura-text-muted uppercase mb-3">Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-aura-text-muted">Organization</span>
                        <span className="text-white">{formData.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-aura-text-muted">Type</span>
                        <span className="text-white capitalize">{formData.org_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-aura-text-muted">Billing</span>
                        <span className={formData.billing_path === 'org_funded' ? 'text-aura-secondary' : 'text-aura-success'}>
                          {formData.billing_path === 'org_funded' ? 'Org-Funded ($49 + $2/athlete)' : 'Athlete-Funded (FREE)'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  disabled={loading || !formData.contact_name || !formData.contact_email || !formData.password}
                  className="w-full h-12 bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider"
                  data-testid="org-register-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Create Organization'
                  )}
                </Button>

                <p className="text-center text-aura-text-subtle text-sm">
                  14-day free trial. No credit card required.
                </p>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OrganizationRegisterPage;
